import { useQuery, useMutation } from "@tanstack/react-query";
import {
  fetch3,
  fetch5,
  fetch2,
  fetch4,
  fetch7,
} from "../commonApis/commonApis";
import { useDispatch } from "react-redux";
import {
  addFleet,
  fleetUpdate,
  setFleetList,
} from "../features/fleet/fleetSlice";
import { setChangeRequestList } from "../features/changeRequest/changeRequestSlice";
import { setOdometerList } from "../features/odometer/odometerSlice";
const url = process.env.REACT_APP_BASE_URL;

interface ApiResponse {
  success: boolean;
  data: any;
  message?: string;
  error?: any;
}

export const useFleetData = (page: number, searchFor: string) => {
  return useQuery({
    queryKey: ["vehiclesData"],
    queryFn: async () => {
      const res = await fetch3(
        `${url}/api/vehicle?page=${page}&searchFor=${searchFor}&type=LM`,
        "get"
      );

      if (!res || !res.data) {
        return res.message;
      }

      return res.data;
    },
    staleTime: 1000 * 60 * 5,
  });
};

export const useOdometerListData = (
  page: number,
  limit: number,
  searchFor: string
) => {
  const dispatch = useDispatch();
  return useQuery({
    queryKey: ["odometerList", page, limit, searchFor],
    queryFn: async () => {
      const res: ApiResponse = await fetch3(
        `${url}/api/odometer/?page=${page}&limit=${limit}&searchFor=${searchFor}`,
        "GET"
      );

      if (!res.success) {
        throw new Error(res.message || "Failed to fetch odometer data");
      }
      dispatch(setOdometerList(res.data));
      return res.data ?? [];
    },
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });
};

export const useReAssignRequestListData = (
  page: number,
  limit: number,
  searchFor: string
) => {
  const dispatch = useDispatch();

  return useQuery({
    queryKey: ["reAssignRequest", page, limit, searchFor], // Unique cache key
    queryFn: async () => {
      const res: ApiResponse = await fetch3(
        `${url}/api/vehicle/reAssignRequests/?page=${page}&limit=${limit}&searchFor=${searchFor}`,
        "GET"
      );

      if (!res.success) {
        throw new Error(
          res.message || "Failed to fetch re-assign request data"
        );
      }
      dispatch(setChangeRequestList(res.data));
      return res.data ?? [];
    },
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });
};

export const useVehicleListData = (
  page: number,
  limit: number,
  searchFor: string
) => {
  const dispatch = useDispatch();
  return useQuery({
    queryKey: ["VehicleList", page, limit, searchFor], // Unique cache key
    queryFn: async () => {
      const res: ApiResponse = await fetch3(
        `${url}/api/vehicle/?page=${page}&limit=${limit}&searchFor=${searchFor}`,
        "GET"
      );

      if (!res.success) {
        throw new Error(res.message || "Failed to fetch fuel data");
      }
      dispatch(setFleetList(res.data));
      return res.data ?? [];
    },
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });
};

interface UpdateVehiclePayload {
  id: string;
  status: any; // Add more statuses if needed
}

export const useUpdateVehicleStatus = () => {
  return useMutation({
    mutationFn: async ({ id, status }: UpdateVehiclePayload) => {
      const res: ApiResponse = await fetch5(
        `${url}/api/vehicle/${id}`,
        "PATCH",
        { status }
      );

      if (!res.success) {
        throw new Error(res.message || "Failed to update vehicle status");
      }

      return res.data;
    },
  });
};

export const useVehicleCreateMutation = () => {
  const dispatch = useDispatch();
  return useMutation({
    mutationFn: async (payload: any) => {
      const res: ApiResponse = await fetch2(`${url}/api/vehicle/`, payload);

      // Manually throw error if success is false
      if (!res.success) {
        throw res.message || res?.error || "Something went wrong";
      }

      return res;
    },
    onSuccess: (data: any) => {
      console.log("✅ Vehicle Created successfully:", data);
      dispatch(addFleet(data));
    },
    onError: (error: any) => {
      console.error("❌ vehicle create request failed:", error.message);
      return error;
    },
  });
};
export const useVehicleAssignMutation = () => {
  return useMutation({
    mutationFn: async (payload: any) => {
      const res: ApiResponse = await fetch2(
        `${url}/api/vehicle/assign/`,
        payload
      );

      // Manually throw error if success is false
      if (!res.success) {
        throw res.message || res?.error || "Something went wrong";
      }

      return res;
    },
    onSuccess: (data: any) => {
      console.log("✅ Vehicle Created successfully:", data);
    },
    onError: (error: any) => {
      console.error("❌ vehicle create request failed:", error.message);
      return error;
    },
  });
};

export const useVehicleReAssignMutation = () => {
  return useMutation({
    mutationFn: async (payload: any) => {
      const res: ApiResponse = await fetch2(
        `${url}/api/vehicle/reAssignVehicle/`,
        payload
      );

      // Manually throw error if success is false
      if (!res.success) {
        throw res.message || res?.error || "Something went wrong";
      }

      return res;
    },
    onSuccess: (data: any) => {
      console.log("✅ Vehicle Created successfully:", data);
    },
    onError: (error: any) => {
      console.error("❌ vehicle create request failed:", error.message);
      return error;
    },
  });
};

export const useUpdateVehicleMutation = () => {
  const dispatch = useDispatch();
  return useMutation({
    mutationFn: async (payload: { id: string; [key: string]: any }) => {
      const { id, ...rest } = payload;
      const res: ApiResponse = await fetch7(
        `${url}/api/vehicle/${id}`,
        "PUT",
        rest
      );

      if (!res.success) {
        throw res.message || res?.error || "Something went wrong";
      }

      return res;
    },
    onSuccess: (data: any) => {
      console.log("✅ Vehicle updated successfully:", data);
      dispatch(fleetUpdate(data));
    },
    onError: (error: any) => {
      console.error("❌ Vehicle update request failed:", error.message);
      return error;
    },
  });
};

//--------------------- * Line Haul Api's Code Start Here's * --------------------//

export const useLineHaulCreateMutation = () => {
  return useMutation({
    mutationFn: async (payload: any) => {
      const res: ApiResponse = await fetch4(
        `${url}/api/lineHaul/company`,
        payload
      );

      // Manually throw error if success is false
      if (!res.success) {
        throw res.message || "Something went wrong";
      }

      return res.data;
    },
    onSuccess: (data: any) => {
      console.log("✅ lineHaul Created successfully:", data);
    },
    onError: (error: any) => {
      console.error("❌ lineHaul create request failed:", error.message);
    },
  });
};

export const useFetchLineHaulData = (
  page: number,
  limit: number,
  searchFor: string
) => {
  return useQuery({
    queryKey: ["lineHaulData", page, limit, searchFor],
    queryFn: async () => {
      const res = await fetch3(
        `${url}/api/lineHaul/company/?page=${page}&limit=${limit}&searchFor=${searchFor}`,
        "get"
      );

      if (!res || !res.data) {
        return res.message;
      }

      return res.data; // 👈 This returns your full user object
    },
    staleTime: 1000 * 60 * 5,
  });
};

export const useFetchLineHaulRequestData = (
  page: number,
  limit: number,
  searchFor: string
) => {
  return useQuery({
    queryKey: ["RequestlineHaulData", page, limit, searchFor],
    queryFn: async () => {
      const res = await fetch3(
        `${url}/api/lineHaul/company-request/?page=${page}&limit=${limit}&searchFor=${searchFor}`,
        "get"
      );

      if (!res || !res.data) {
        return res.message;
      }

      return res.data;
    },
    staleTime: 1000 * 60 * 5,
  });
};

interface UpdateLineHaulPayload {
  id: any;
  status: any; // Add more statuses if needed
}

type UpdateLineHauleDetails = {
  companyName: string;
  contactPerson: string;
  mobileNumber: string;
  email: string;
  address: string;
};

export const useUpdateLineHaulStatus = () => {
  return useMutation({
    mutationFn: async ({ id, status }: UpdateLineHaulPayload) => {
      const res: ApiResponse = await fetch5(
        `${url}/api/lineHaul/company/${id}`,
        "PATCH",
        { status }
      );

      if (!res.success) {
        throw new Error(res.message || "Failed to update vehicle status");
      }

      return res.data;
    },
  });
};

export const useLineHaulUpdateMutation = (id: string) => {
  return useMutation({
    mutationFn: async (payload: UpdateLineHauleDetails) => {
      const res: ApiResponse = await fetch5(
        `${url}/api/lineHaul/company/${id}`,
        "PUT",
        payload
      );

      if (!res.success) {
        throw res.message || "Something went wrong";
      }

      return res.data;
    },
    onSuccess: (data: any) => {
      console.log("✅ Staff Created successfully:", data);
    },
    onError: (error: any) => {
      console.error("❌ staff create request failed:", error.message);
    },
  });
};

export const useAllCompanyFetchData = () => {
  return useQuery({
    queryKey: ["companyData"],
    queryFn: async () => {
      const res = await fetch3(`${url}/api/lineHaul/company-all`, "get");

      if (!res || !res.data) {
        return res.message;
      }

      return res.data; // 👈 This returns your full user object
    },
    staleTime: 1000 * 60 * 5,
  });
};

export const useSendRequestMutation = () => {
  return useMutation({
    mutationFn: async (payload: any) => {
      const res: ApiResponse = await fetch4(
        `${url}/api/lineHaul/company-request/`,
        payload
      );

      // Manually throw error if success is false
      if (!res.success) {
        throw res.message || "Something went wrong";
      }

      return res.data;
    },
    onSuccess: (data: any) => {
      console.log("✅ Request Send successfully:", data);
    },
    onError: (error: any) => {
      console.error("❌ Request Send request failed:", error.message);
    },
  });
};
export const useFillInfoSendRequestMutation = () => {
  return useMutation({
    mutationFn: async (payload: any) => {
      const res: ApiResponse = await fetch2(
        `${url}/api/lineHaul/linehaul-request/`,
        payload
      );

      // Manually throw error if success is false
      if (!res.success) {
        throw res.message || "Something went wrong";
      }

      return res.data;
    },
    onSuccess: (data: any) => {
      console.log("✅ Create successfully:", data);
    },
    onError: (error: any) => {
      console.error("❌ Create request failed:", error.message);
    },
  });
};

export const useAllLinehaulRequestFetchData = (
  page: number,
  limit: number,
  searchFor: string
) => {
  return useQuery({
    queryKey: ["companyData", page, limit, searchFor],
    queryFn: async () => {
      const res = await fetch3(
        `${url}/api/lineHaul/linehaul-request/?page=${page}&limit=${limit}&searchFor=${searchFor}`,
        "get"
      );

      if (!res || !res.data) {
        return res.message;
      }

      return res.data; // 👈 This returns your full user object
    },
    staleTime: 1000 * 60 * 5,
  });
};

export const useFetchLineHaulRequestById = (
  page: number,
  limit: number,
  searchFor: string,
  id: string
) => {
  return useQuery({
    queryKey: ["companyData", page, limit, searchFor, id],
    queryFn: async () => {
      if (!id) {
        return null;
      }

      const res = await fetch3(
        `${url}/api/lineHaul/linehaul-request-form/${id}?page=${page}&limit=${limit}&searchFor=${searchFor}&type=LM`,
        "get"
      );

      if (!res || !res.data) {
        return res.message;
      }

      return res.data;
    },
    enabled: !!id,
    staleTime: 1000 * 60 * 5,
  });
};

export const useApprovedMutation = () => {
  return useMutation({
    mutationFn: async (payload: any) => {
      const res: ApiResponse = await fetch4(
        `${url}/api/lineHaul/linehaul-request-approve`,
        payload
      );

      if (!res.success) {
        throw res.message || "Something went wrong";
      }

      return res.data;
    },
    onSuccess: (data: any) => {
      console.log("✅ Companies Quotation successfully:", data);
    },
    onError: (error: any) => {
      console.error("❌ Companies Quotation request failed:", error.message);
    },
  });
};
